/*
This file is located in the main branch
Last time the file was updated (UTC): [11.12.2024 08:50:34]
Permissions to update outside the main branch: [false]
*/

import {
    DynamicListRowWrapperDefault,
    DynamicListWrapperProps,
    requiredRule,
    useUpdateEffect,
    ValidatedDynamicList,
    ValidatedDynamicListFormComponentProps,
    ValidatedInput,
} from '@finbeat/react-toolkit';
import { OutleHttpHeadersList } from 'models/api/outlet';
import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import { OutletFormType } from './OutletChangeModal';
import TwoColumnWrapper from './TwoColumnWrapper';

type TabApiSettingsProps = {
    isOpen?: boolean;
    formHasRole: boolean;
};

const TabApiSettings = ({ formHasRole }: TabApiSettingsProps) => {
    const { t } = useTranslation();
    const { watch, setValue } = useFormContext<OutletFormType>();
    const useHttpHeaders = watch('apiSettings.useHttpHeaders');
    const useProxy = watch('apiSettings.useProxy');

    useUpdateEffect(() => {
        setValue('apiSettings.httpHeaders', [{ header: '', value: '' }]);
    }, [useHttpHeaders]);

    useUpdateEffect(() => {
        setValue('apiSettings.proxySettings.ip', '');
        setValue('apiSettings.proxySettings.port', 0);
        setValue('apiSettings.proxySettings.authenticationMethod', '');
        setValue('apiSettings.proxySettings.login', '');
        setValue('apiSettings.proxySettings.password', '');
    }, [useProxy]);

    const formComponent = useCallback(
        (props: ValidatedDynamicListFormComponentProps) => (
            <Form {...props} formHasRole={formHasRole} />
        ),
        [formHasRole]
    );

    const wrapper = useCallback(
        (props: DynamicListWrapperProps) => (
            <TwoColumnWrapper {...props} col1Label="BackOfficeHeader" col2Label="BackOfficeValue" />
        ),
        []
    );

    const chromeDisableAutofillProp = {
        autocomplete: 'one-time-code',
    };

    return (
        <>
            <Row>
                <Col>
                    <ValidatedInput<OutletFormType>
                        name="apiSettings.url"
                        label={t('BackOfficeUrl')}
                        disabled={!formHasRole}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <ValidatedInput<OutletFormType>
                        name="apiSettings.login"
                        label={t('BackOfficeUsername')}
                        disabled={!formHasRole}
                    />
                </Col>
                <Col>
                    <ValidatedInput<OutletFormType>
                        type="password"
                        name="apiSettings.password"
                        label={t('BackOfficePassword')}
                        disabled={!formHasRole}
                        {...chromeDisableAutofillProp}
                    />
                </Col>
            </Row>
            <hr />
            <Row className="mb-3">
                <Col>
                    <ValidatedInput<OutletFormType>
                        id="apiSettingsUseHttpHeaders"
                        type="checkbox"
                        name="apiSettings.useHttpHeaders"
                        label={t('BackOfficeAddHttpHeaders')}
                        disabled={!formHasRole}
                    />
                </Col>
            </Row>
            {useHttpHeaders && (
                <Row>
                    <Col>
                        <ValidatedDynamicList<OutletFormType, OutleHttpHeadersList>
                            formComponent={formComponent}
                            wrapper={wrapper}
                            prepend={false}
                            emptyRecord={{
                                header: '',
                                value: '',
                            }}
                            name="apiSettings.httpHeaders"
                            compareFunc={(a, b) => a?.header === b?.header}
                            rowWrapper={(props) => (
                                <DynamicListRowWrapperDefault
                                    {...props}
                                    disabled={props.disabled || props.idx === 0}
                                />
                            )}
                        />
                    </Col>
                </Row>
            )}
            <hr />
            <Row className="mb-3">
                <Col>
                    <ValidatedInput<OutletFormType>
                        id="apiSettingsUseProxy"
                        type="checkbox"
                        name="apiSettings.useProxy"
                        label={t('BackOfficeUseProxy')}
                        disabled={!formHasRole}
                    />
                </Col>
            </Row>
            {useProxy && (
                <>
                    <Row>
                        <Col>
                            <ValidatedInput<OutletFormType>
                                name="apiSettings.proxySettings.ip"
                                label={t('BackOfficeIp')}
                                disabled={!formHasRole}
                                rules={{
                                    required: requiredRule,
                                }}
                            />
                        </Col>
                        <Col>
                            <ValidatedInput<OutletFormType>
                                type="number"
                                name="apiSettings.proxySettings.port"
                                label={t('BackOfficePort')}
                                disabled={!formHasRole}
                                rules={{
                                    required: requiredRule,
                                }}
                            />
                        </Col>
                        <Col>
                            <ValidatedInput<OutletFormType>
                                name="apiSettings.proxySettings.authenticationMethod"
                                label={t('BackOfficeAuthMethod')}
                                disabled={!formHasRole}
                                rules={{
                                    required: requiredRule,
                                }}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <ValidatedInput<OutletFormType>
                                name="apiSettings.proxySettings.login"
                                label={t('BackOfficeUsername')}
                                disabled={!formHasRole}
                                rules={{
                                    required: requiredRule,
                                }}
                                {...chromeDisableAutofillProp}
                            />
                        </Col>
                        <Col>
                            <ValidatedInput<OutletFormType>
                                type="password"
                                name="apiSettings.proxySettings.password"
                                label={t('BackOfficePassword')}
                                disabled={!formHasRole}
                                rules={{
                                    required: requiredRule,
                                }}
                                {...chromeDisableAutofillProp}
                            />
                        </Col>
                    </Row>
                </>
            )}
            <hr />
            <Row>
                <Col>
                    <ValidatedInput<OutletFormType>
                        id="apiSettingsUseOrderActualization"
                        type="checkbox"
                        name="apiSettings.useOrderActualization"
                        label={t('BackOfficeOrderActualization')}
                        disabled={!formHasRole}
                    />
                </Col>
            </Row>
        </>
    );
};

const Form = ({
    idx,
    formHasRole,
}: ValidatedDynamicListFormComponentProps & { formHasRole: boolean }) => {
    const { t } = useTranslation();
    return (
        <>
            <td>
                <ValidatedInput<OutletFormType>
                    name={`apiSettings.httpHeaders.${idx}.header`}
                    label={t('BackOfficeHeader')}
                    disabled={!formHasRole}
                    rules={{
                        required: requiredRule,
                    }}
                />
            </td>
            <td>
                <ValidatedInput<OutletFormType>
                    name={`apiSettings.httpHeaders.${idx}.value`}
                    label={t('BackOfficeValue')}
                    disabled={!formHasRole}
                    rules={{
                        required: requiredRule,
                    }}
                />
            </td>
        </>
    );
};

export default TabApiSettings;
